import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LINKS } from '../constants/links';
import { Sun, Moon, Menu, X, User } from 'lucide-react';

interface NavBarProps {
  isDark: boolean;
  setIsDark: React.Dispatch<React.SetStateAction<boolean>>;
  isAuthenticated: boolean;
  onLogout: () => void;
}

export default function NavBar({
  isDark,
  setIsDark,
  isAuthenticated,
  onLogout,
}: NavBarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `py-1 px-2 font-medium uppercase tracking-wide transition ${
      isActive ? 'text-blue-500' : 'hover:text-blue-500'
    }`;

  const mobileNavLinkClass = ({ isActive }: { isActive: boolean }) =>
    `uppercase font-medium tracking-wide transition ${
      isActive ? 'text-blue-500' : 'hover:text-blue-500'
    }`;

  const handleLogout = () => {
    setShowLogoutPopup(true);
    setProfileMenuOpen(false);
  };

  const confirmLogout = () => {
    onLogout();
    setShowLogoutPopup(false);
    setProfileMenuOpen(false);
    setMenuOpen(false);
  };

  const cancelLogout = () => {
    setShowLogoutPopup(false);
  };

  return (
    <nav className="fixed top-0 left-0 w-full z-[2000] bg-white/80 dark:bg-neutral-900/80 backdrop-blur-md text-gray-900 dark:text-white shadow">
      <div className="flex items-center justify-between px-6 max-h-[85px] h-[85px] max-w-7xl mx-auto w-full">
        <div className="flex items-center gap-2 flex-shrink-0">
          <NavLink to="/" className="flex items-center gap-[6px]">
            <img
              src={
                isDark
                  ? '/assets/FoMo-logo-light.webp'
                  : '/assets/FoMo-logo.webp'
              }
              alt="FoMo Logo"
              style={{ height: '72px' }}
              className="h-auto"
            />
            <span className="font-sans text-[26px] font-bold tracking-tight text-gray-700 dark:text-white">
              FoMo Dataset
            </span>
          </NavLink>
        </div>

        <div className="hidden tablet:flex justify-center items-center flex-1 gap-5 text-lg whitespace-nowrap">
          <NavLink to={LINKS.OVERVIEW} className={navLinkClass}>
            Overview
          </NavLink>
          <NavLink to={LINKS.DEPLOYMENTS} className={navLinkClass}>
            Deployments
          </NavLink>
          <NavLink to={LINKS.DOWNLOAD} className={navLinkClass}>
            Download
          </NavLink>
          {/*          <NavLink to={LINKS.SUBMISSION} className={navLinkClass}>
            Submission
          </NavLink>*/}
          <NavLink to={LINKS.DEPLOYMENT_LOGS} className={navLinkClass}>
            Logbook
          </NavLink>
          <NavLink
            to={'https://github.com/norlab-ulaval/fomo-sdk'}
            className={navLinkClass}
          >
            Toolkit
          </NavLink>
          <NavLink to={LINKS.CONTACT} className={navLinkClass}>
            Contact
          </NavLink>

          {isAuthenticated ? (
            <div className="relative">
              <button
                onClick={() => setProfileMenuOpen((prev) => !prev)}
                className="w-10 h-10 rounded-full flex items-center justify-center border border-gray-400"
              >
                <User className="w-8 h-8 text-gray-600 dark:text-gray-300" />
              </button>

              {profileMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-neutral-800 rounded-lg shadow-lg py-2 z-[9999]">
                  <NavLink
                    to={LINKS.PROFILE}
                    onClick={() => setProfileMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-neutral-700 rounded"
                  >
                    <User className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                    <span className="font-medium">Profile</span>
                  </NavLink>
                  <button
                    onClick={() => {
                      handleLogout();
                      setProfileMenuOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-700"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <NavLink to={LINKS.SIGNIN} className={navLinkClass}>
              Login
            </NavLink>
          )}
        </div>

        <div className="flex items-center gap-4 tablet:gap-2 flex-shrink-0">
          <button
            onClick={() => setIsDark(!isDark)}
            className="p-3 rounded-md hover:bg-gray-200 dark:hover:bg-darkhover"
          >
            {isDark ? <Sun size={25} /> : <Moon size={25} />}
          </button>

          <button
            className="tablet:hidden p-2"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="tablet:hidden flex flex-col items-center gap-4 py-4 text-lg bg-white/70 dark:bg-neutral-900/70 backdrop-blur border-t border-gray-300 dark:border-gray-700">
          <NavLink
            to={LINKS.OVERVIEW}
            onClick={() => setMenuOpen(false)}
            className={mobileNavLinkClass}
          >
            Overview
          </NavLink>
          <NavLink
            to={LINKS.DEPLOYMENTS}
            onClick={() => setMenuOpen(false)}
            className={mobileNavLinkClass}
          >
            Deployments
          </NavLink>
          <NavLink
            to={LINKS.DOWNLOAD}
            onClick={() => setMenuOpen(false)}
            className={mobileNavLinkClass}
          >
            Download
          </NavLink>
          {/*          <NavLink
            to={LINKS.SUBMISSION}
            onClick={() => setMenuOpen(false)}
            className={mobileNavLinkClass}
          >
            Submission
          </NavLink>*/}
          <NavLink
            to={LINKS.DEPLOYMENT_LOGS}
            onClick={() => setMenuOpen(false)}
            className={mobileNavLinkClass}
          >
            Logbook
          </NavLink>
          <NavLink
            to={'https://github.com/norlab-ulaval/fomo-sdk'}
            className={mobileNavLinkClass}
          >
            Toolkit
          </NavLink>
          <NavLink
            to={LINKS.CONTACT}
            onClick={() => setMenuOpen(false)}
            className={mobileNavLinkClass}
          >
            Contact
          </NavLink>

          {isAuthenticated ? (
            <div className="flex flex-col items-center gap-2 border-t border-gray-300 dark:border-gray-700 pt-2">
              <NavLink
                to={LINKS.PROFILE}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-neutral-700 rounded"
              >
                <User className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                <span className="font-medium">Profile</span>
              </NavLink>
              <button
                onClick={() => {
                  handleLogout();
                  setMenuOpen(false);
                }}
                className="px-4 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-neutral-700 rounded"
              >
                Logout
              </button>
            </div>
          ) : (
            <NavLink
              to={LINKS.SIGNIN}
              onClick={() => setMenuOpen(false)}
              className={mobileNavLinkClass}
            >
              Login
            </NavLink>
          )}
        </div>
      )}

      {showLogoutPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-neutral-900 p-6 rounded shadow-lg text-center">
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              Are you sure you want to logout?
            </p>
            <div className="mt-4 flex justify-center gap-4">
              <button
                onClick={cancelLogout}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
